/**
 * useVendors.ts - Custom Hook for Vendor State Management
 */

import { useState, useEffect } from 'react';
import { Vendor } from '../utils/constants';
import vendorService, { CreateMenuItemInput, UpdateMenuItemInput } from '../services/vendorService';

interface VendorData {
  name: string;
  sections: string[];
  cuisine_type?: string | null;
}

interface MenuItemData extends CreateMenuItemInput {}

interface UseVendorsReturn {
  vendors: Vendor[];
  loading: boolean;
  error: string | null;
  addVendor: (vendorData: VendorData) => Promise<void>;
  updateVendorSections: (vendorId: string, sections: string[]) => Promise<void>;
  updateVendor: (
    vendorId: string,
    data: Partial<
      Pick<
        Vendor,
        'name' | 'sections' | 'cuisine_type' | 'description' | 'image' | 'status' | 'currentWaitMinutes'
      >
    >
  ) => Promise<Vendor>;
  addMenuItem: (vendorId: string, menuItemData: MenuItemData) => Promise<Vendor>;
  updateMenuItem: (vendorId: string, itemId: string, menuItemData: MenuItemData) => Promise<Vendor>;
  deleteMenuItem: (vendorId: string, itemId: string) => Promise<Vendor>;
  deleteVendor: (vendorId: string) => Promise<void>;
  getVendorById: (vendorId: string) => Vendor | null;
  updateVendorRevenue: (vendorId: string, revenue: string) => Promise<void>;
  refreshVendors: () => Promise<void>;
}

const useVendors = (): UseVendorsReturn => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await vendorService.getAll();
      setVendors(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load vendors';
      setError(message);
      console.error('Error loading vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshVendors = async (): Promise<void> => {
    await loadVendors();
  };

  const addVendor = async (vendorData: VendorData): Promise<void> => {
    try {
      const newVendor = await vendorService.create(vendorData);
      setVendors(prevVendors => [...prevVendors, newVendor]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add vendor';
      setError(message);
      throw err;
    }
  };

  const updateVendorSections = async (vendorId: string, sections: string[]): Promise<void> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      const updatedVendor = await vendorService.update(vendor.id, { sections });
      setVendors(prevVendors =>
        prevVendors.map(v => v.id === vendorId ? updatedVendor : v)
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update vendor sections';
      setError(message);
      throw err;
    }
  };

  const updateVendor = async (
    vendorId: string,
    data: Partial<
      Pick<
        Vendor,
        'name' | 'sections' | 'cuisine_type' | 'description' | 'image' | 'status' | 'currentWaitMinutes'
      >
    >
  ): Promise<Vendor> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      const updatedVendor = await vendorService.update(vendor.id, data);
      setVendors(prevVendors =>
        prevVendors.map(v => v.id === vendorId ? updatedVendor : v)
      );
      return updatedVendor;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update vendor';
      setError(message);
      throw err;
    }
  };

  const addMenuItem = async (vendorId: string, menuItemData: MenuItemData): Promise<Vendor> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      const updatedVendor = await vendorService.addMenuItem(vendor.id, menuItemData);
      setVendors(prevVendors =>
        prevVendors.map(v => v.id === vendorId ? updatedVendor : v)
      );
      return updatedVendor;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add menu item';
      setError(message);
      throw err;
    }
  };

  const updateMenuItem = async (
    vendorId: string,
    itemId: string,
    menuItemData: MenuItemData
  ): Promise<Vendor> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      const menuItem = vendor.menuItems.find(item => item.id === itemId);
      if (!menuItem) throw new Error('Menu item not found');

      const updatedVendor = await vendorService.updateMenuItem(
        vendor.id,
        menuItem.id,
        menuItemData as UpdateMenuItemInput
      );
      setVendors(prevVendors =>
        prevVendors.map(v => v.id === vendorId ? updatedVendor : v)
      );
      return updatedVendor;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update menu item';
      setError(message);
      throw err;
    }
  };

  const deleteMenuItem = async (vendorId: string, itemId: string): Promise<Vendor> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      const menuItem = vendor.menuItems.find(item => item.id === itemId);
      if (!menuItem) throw new Error('Menu item not found');

      const updatedVendor = await vendorService.deleteMenuItem(vendor.id, menuItem.id);
      setVendors(prevVendors =>
        prevVendors.map(v => v.id === vendorId ? updatedVendor : v)
      );
      return updatedVendor;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete menu item';
      setError(message);
      throw err;
    }
  };

  const deleteVendor = async (vendorId: string): Promise<void> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      await vendorService.delete(vendor.id);
      setVendors(prevVendors => prevVendors.filter(v => v.id !== vendorId));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete vendor';
      setError(message);
      throw err;
    }
  };

  const getVendorById = (vendorId: string): Vendor | null => {
    return vendors.find(vendor => vendor.id === vendorId) || null;
  };

  const updateVendorRevenue = async (vendorId: string, revenue: string): Promise<void> => {
    try {
      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) throw new Error('Vendor not found');

      const revenueNumber = parseFloat(revenue.replace(/[$,]/g, ''));

      const updatedVendor = await vendorService.update(vendor.id, { revenue: revenueNumber });
      setVendors(prevVendors =>
        prevVendors.map(v => v.id === vendorId ? updatedVendor : v)
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update vendor revenue';
      setError(message);
      throw err;
    }
  };

  return {
    vendors,
    loading,
    error,
    addVendor,
    updateVendorSections,
    updateVendor,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    deleteVendor,
    getVendorById,
    updateVendorRevenue,
    refreshVendors
  };
};

export default useVendors;
